import { useMutation } from "@apollo/client";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { TierIIReportKind } from "encamp-prisma/generated/client/client";
import { AutomationRunner } from "encampy";
import {
  AlternateId,
  CredentialSecurityQuestion,
  PortalType,
} from "generated-graphql/graphql";
import { useCallback, useMemo, useRef, useState } from "react";
import { getEncampyLoginAutomation } from "util/encampy/portalLogin";
import { ACQUIRE_CREDENTIAL_LOCK } from "./api";
import {
  EncampyAutomationLauncher,
  EncampyAutomationLauncherProps,
  EncampyLaunchData,
} from "./AutomationLauncher";

interface LockConflictInfo {
  lockedBy: string;
  lockExpiresAt: string;
  lockedUsername: string;
  lockedState: string;
  message: string;
  allLocked: boolean;
}

type MinimalPortalCredential = {
  credential?: {
    username: string;
    password: string;
    securityQuestions?: CredentialSecurityQuestion[] | null;
    portal?: {
      type: PortalType;
      url?: string | null;
      portalTech?: string | null;
    } | null;
  } | null;
};

export interface EncampyPortalLoginLauncherProps
  extends Omit<
    EncampyAutomationLauncherProps,
    "launchData" | "onBeforeLaunch"
  > {
  portalCredential?: MinimalPortalCredential | null;
  facilityAlternateIds?: AlternateId[] | null;
  reportingYear?: number;
  reportKind?: TierIIReportKind;
  credentialId?: string;
  steersCn?: string;
  onLockAcquired?: (lockId: string) => void;
}

export function EncampyPortalLoginLauncher({
  portalCredential,
  facilityAlternateIds,
  reportingYear,
  reportKind,
  credentialId,
  steersCn,
  onLockAcquired,
  disabled = false,
  disabledReason,
  onError,
  ...rest
}: EncampyPortalLoginLauncherProps) {
  const [acquireCredentialLock] = useMutation(ACQUIRE_CREDENTIAL_LOCK);
  const [lockConflict, setLockConflict] = useState<LockConflictInfo | null>(
    null
  );
  const conflictResolverRef = useRef<
    ((continueAnyway: boolean) => void) | null
  >(null);

  const launchData = useMemo((): EncampyLaunchData | null => {
    if (!portalCredential?.credential?.portal?.url) {
      return null;
    }

    const singlePhaseAutomation = portalCredential
      ? getEncampyLoginAutomation(
          portalCredential.credential,
          portalCredential.credential.portal,
          facilityAlternateIds,
          reportingYear,
          reportKind
        )
      : null;

    if (!singlePhaseAutomation) {
      return null;
    }

    return {
      url: portalCredential.credential.portal.url,
      automation: async (runner: AutomationRunner) => {
        await runner.run(singlePhaseAutomation);
      },
    };
  }, [portalCredential, facilityAlternateIds, reportingYear, reportKind]);

  const buildLaunchData = useCallback(
    (portalCredential: MinimalPortalCredential): EncampyLaunchData | null => {
      if (!portalCredential?.credential?.portal?.url) {
        return null;
      }

      const automation = getEncampyLoginAutomation(
        portalCredential.credential,
        portalCredential.credential.portal,
        facilityAlternateIds,
        reportingYear,
        reportKind
      );

      return {
        url: portalCredential.credential.portal.url,
        automation: async (runner: AutomationRunner) => {
          await runner.run(automation);
        },
      };
    },
    [facilityAlternateIds, reportingYear, reportKind]
  );

  const showLockConflictDialog = useCallback(
    (info: LockConflictInfo): Promise<boolean> => {
      return new Promise((resolve) => {
        conflictResolverRef.current = resolve;
        setLockConflict(info);
      });
    },
    []
  );

  const handleConflictResponse = useCallback((continueAnyway: boolean) => {
    conflictResolverRef.current?.(continueAnyway);
    conflictResolverRef.current = null;
    setLockConflict(null);
  }, []);

  const onBeforeLaunch =
    useCallback(async (): Promise<EncampyLaunchData | null> => {
      // If no credentialId and no steersCn, skip locking entirely (backward compatible)
      if (!credentialId && !steersCn) {
        return launchData;
      }

      try {
        const { data } = await acquireCredentialLock({
          variables: {
            input: {
              credentialId: credentialId ?? undefined,
              steersCn,
            },
          },
        });

        const result = data?.acquireCredentialLock;

        if (!result?.success) {
          // If we have lock conflict info, show the confirmation dialog
          if (result?.lockedBy || result?.lockedState) {
            const allLocked = Boolean(
              steersCn && !result.credentialId && result.lockedState
            );
            const continueAnyway = await showLockConflictDialog({
              lockedBy: result.lockedBy ?? "another user",
              lockExpiresAt: result.lockExpiresAt ?? "",
              lockedUsername: result.lockedUsername ?? "",
              lockedState: result.lockedState ?? "",
              message: result.message ?? "Credential is currently locked",
              allLocked,
            });

            if (continueAnyway) {
              return launchData;
            }
            return null;
          }

          onError?.(result?.message ?? "Failed to acquire credential lock");
          return null;
        }

        if (result.lockId) {
          onLockAcquired?.(result.lockId);
        }

        // Check if the server returned a different credential (TX swap case)
        const lockedCred = result.credential;
        if (lockedCred && result.credentialId !== credentialId) {
          const swappedPortalCredential: MinimalPortalCredential = {
            credential: {
              ...portalCredential?.credential,
              username: lockedCred.username,
              password: lockedCred.password,
              securityQuestions: lockedCred.securityQuestions,
            },
          };

          const swappedLaunchData = buildLaunchData(swappedPortalCredential);
          if (!swappedLaunchData) {
            onError?.("Failed to build automation for swapped credential");
            return null;
          }
          return swappedLaunchData;
        }

        // Same credential or no credential data returned — use original launch data
        return launchData;
      } catch (error) {
        onError?.(
          error instanceof Error
            ? error.message
            : "Failed to acquire credential lock"
        );
        return null;
      }
    }, [
      credentialId,
      steersCn,
      launchData,
      acquireCredentialLock,
      onError,
      onLockAcquired,
      buildLaunchData,
      showLockConflictDialog,
      portalCredential,
    ]);

  // Synchronous validation - no async loading/error states needed
  const computedDisabledReason = useMemo((): string | undefined => {
    if (disabled && disabledReason) {
      return disabledReason;
    }
    if (!launchData) {
      return "Portal credential URL is required";
    }
    if (disabled) {
      return "Portal login is disabled";
    }
    return undefined;
  }, [launchData, disabled, disabledReason]);

  // Provide placeholder for disabled state
  const effectiveLaunchData: EncampyLaunchData = launchData ?? {
    url: portalCredential?.credential?.portal?.url || "",
    automation: async () => {
      // No-op automation for disabled state
    },
  };

  const formattedExpiration = lockConflict?.lockExpiresAt
    ? new Date(lockConflict.lockExpiresAt).toLocaleString()
    : "unknown";

  const usernameDisplay = lockConflict?.lockedUsername
    ? ` \`${lockConflict.lockedUsername}\``
    : "";

  const stateDisplay = lockConflict?.lockedState
    ? `${lockConflict.lockedState} `
    : "";

  return (
    <>
      <EncampyAutomationLauncher
        launchData={effectiveLaunchData}
        disabled={disabled || !launchData}
        disabledReason={computedDisabledReason}
        onError={onError}
        onBeforeLaunch={onBeforeLaunch}
        {...rest}
      />
      <Dialog
        open={lockConflict !== null}
        onClose={() => handleConflictResponse(false)}
      >
        <DialogTitle>Credential Locked</DialogTitle>
        <DialogContent>
          {lockConflict?.allLocked ? (
            <>
              <DialogContentText>
                All {stateDisplay}portal credentials are currently locked. The
                lock expiring soonest is{usernameDisplay}, currently locked by{" "}
                {lockConflict.lockedBy} with an expiration of{" "}
                {formattedExpiration}.
              </DialogContentText>
              <DialogContentText sx={{ mt: 2 }}>
                If you continue, you will not acquire a credential lock, the
                existing user will be kicked from STEERS, and another user or
                automation may disrupt your session.
              </DialogContentText>
            </>
          ) : (
            <>
              <DialogContentText>
                The {stateDisplay}portal credential{usernameDisplay} is
                currently locked by {lockConflict?.lockedBy ?? "another user"}{" "}
                with an expiration of {formattedExpiration}.
              </DialogContentText>
              <DialogContentText sx={{ mt: 2 }}>
                If you continue, you will not acquire a credential lock. This
                may disrupt the existing user&apos;s session, and another user
                or automation may disrupt yours.
              </DialogContentText>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleConflictResponse(false)}>Cancel</Button>
          <Button onClick={() => handleConflictResponse(true)} color="warning">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
